import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";

import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import ChipItemGroupLoading from "./ChipItemGroupLoading";
import EditableChipItem from "./EditableChipItem";
import SelectableChipItem from "./SelectableChipItem";
import { StyledAddNewChipButton, StyledListItemWrapper } from "./styles";

interface IProps<T = string> {
    isLoading?: boolean;
    addButtonTooltip?: string;
    items: readonly T[];
    selectedItems: readonly T[];
    onSelectItems: (items: T[], clickedItem: T, isSelected: boolean) => void;
    getItemId?: (item: T) => string;
    getItemLabel?: (item: T) => string;
    onAddItem?: (itemLabel: string) => void;
}

const MultiSelectChipsGroup = <T = string,>({
    isLoading,
    addButtonTooltip,
    items,
    selectedItems,
    onSelectItems,
    getItemId = (item: T) => String(item),
    getItemLabel = (item: T) => String(item),
    onAddItem,
}: IProps<T>): ReactElement => {
    const [isAddNew, setIsAddNew] = useState(false);

    const selectedItemsIds = useMemo(() => selectedItems.map(getItemId), [selectedItems]);

    useEffect(() => {
        setIsAddNew(false);
    }, [items.length]);

    const handleSelectItem = (item: T) => {
        const itemId = getItemId(item);

        if (selectedItemsIds.includes(itemId)) {
            onSelectItems(
                selectedItems.filter((selectedItem: T) => getItemId(selectedItem) !== itemId),
                item,
                false,
            );

            return;
        }

        onSelectItems([...selectedItems, item], item, true);
    };

    const handleAddItem = (itemLabel: string): void => {
        const existingOne: T | undefined = items.find((item) => getItemLabel(item) === itemLabel);
        const selectedOne: T | undefined = selectedItems.find((item) => getItemLabel(item) === itemLabel);

        if (existingOne) {
            setIsAddNew(false);

            if (!selectedOne) {
                onSelectItems([...selectedItems, existingOne], existingOne, true);
            }

            return;
        } else {
            onAddItem?.(itemLabel);
        }
    };

    return (
        <Flex
            gap="8px"
            flexWrap="wrap"
        >
            {isLoading ? <ChipItemGroupLoading hasAddButton={!!onAddItem} /> : null}

            {items.map((item) => (
                <StyledListItemWrapper key={getItemId(item)}>
                    <SelectableChipItem
                        isSelected={selectedItemsIds.includes(getItemId(item))}
                        label={getItemLabel(item)}
                        onClick={() => handleSelectItem(item)}
                    />
                </StyledListItemWrapper>
            ))}

            {!isLoading && isAddNew ? (
                <EditableChipItem
                    isLoading={isLoading}
                    onCloseClick={() => setIsAddNew(false)}
                    onApplyClick={handleAddItem}
                />
            ) : null}

            {!isLoading && !isAddNew && onAddItem ? (
                <StyledListItemWrapper>
                    <Tooltip
                        content={addButtonTooltip}
                        enabled={!!addButtonTooltip}
                    >
                        <StyledAddNewChipButton
                            iconName="add"
                            type="flat"
                            iconSize="xs"
                            onClick={() => setIsAddNew(true)}
                        />
                    </Tooltip>
                </StyledListItemWrapper>
            ) : null}
        </Flex>
    );
};

export default MultiSelectChipsGroup;
